import DOMPurify from 'dompurify'

/**
 * HTML Sanitization Utility
 * Provides secure HTML sanitization to prevent XSS attacks
 */

// Default sanitization config for user content
const defaultConfig = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'blockquote', 'a', 'span', 'div'
  ],
  ALLOWED_ATTR: ['href', 'target', 'class', 'style'],
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  ADD_ATTR: ['target'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
  FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button', 'select', 'textarea']
}

// Strict config for comments (more restrictive)
const commentsConfig = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'span', 'a'],
  ALLOWED_ATTR: ['href', 'target'],
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  ADD_ATTR: ['target'],
  FORBID_ATTR: ['style', 'class', 'id'],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'img']
}

/**
 * Sanitize HTML content for safe rendering
 */
export const sanitizeHtml = (
  dirty: string, 
  options: 'default' | 'comments' | 'strict' = 'default'
): string => {
  if (!dirty || typeof dirty !== 'string') {
    return ''
  }

  let config = defaultConfig
  if (options === 'comments') {
    config = commentsConfig
  } else if (options === 'strict') {
    config = { 
      ALLOWED_TAGS: [], 
      ALLOWED_ATTR: [],
      ALLOWED_URI_REGEXP: /^$/,
      ADD_ATTR: [],
      FORBID_ATTR: ['style', 'class', 'id', 'onclick', 'onerror', 'onload'],
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'img', 'video', 'audio']
    }
  }

  return DOMPurify.sanitize(dirty, config)
}

/**
 * Sanitize and create safe HTML props for React
 */
export const createSafeHtml = (
  dirty: string, 
  options: 'default' | 'comments' | 'strict' = 'default'
): { __html: string } => {
  return { __html: sanitizeHtml(dirty, options) }
}

/**
 * Strip all HTML tags and return plain text
 */
export const stripHtml = (dirty: string): string => {
  if (!dirty || typeof dirty !== 'string') {
    return ''
  }
  
  return DOMPurify.sanitize(dirty, { 
    ALLOWED_TAGS: [], 
    ALLOWED_ATTR: [] 
  })
}

/**
 * Validate if content is safe (returns true if no dangerous content found)
 */
export const isContentSafe = (content: string): boolean => {
  if (!content) return true
  
  const sanitized = sanitizeHtml(content, 'strict')
  return sanitized === stripHtml(content)
}